<template>
    <section class="clickout-box" :class="boxClasses">
        <img
            v-if="imageUrl"
            :src="imageUrl"
            alt="clickout-image"
            class="clickout-box__image tw-block tw-mx-auto tw-h-auto lg:tw-flex-shrink-0 tw-w-[190px]"
            loading="lazy"
        >

        <p class="clickout-box__description" :class="descriptionClasses">
            {{ description }}
        </p>

        <dk-button
            :href="url"
            target="_blank"
            rel="nofollow"
            class="tw-w-full lg:tw-w-auto lg:tw-shrink-0 lg:tw-px-10"
            :class="ctaClasses"
            variant="accent"
            :is-small="isCtaSmall"
            @click="onClickRegisterGAEvent()"
        >
            <span class="tw-mr-1">{{ ctaText }}</span>
            <dk-icon v-if="showIcon" name="external-link" size="xs" />
        </dk-button>
    </section>
</template>

<script>
import gaEventHandler from '@v4/common/components/mixins/google-analytics.js';

/**
 * A box to display a CTA button in order to
 * to send traffic from DriveK pages to OEM's websites
 *
 * @version 4.0.0
 * @example ./../examples/ClickoutBox.md
 */
export default {
    name: 'ClickoutBox',
    components: {
        DkIcon: () => ({
            component: import(/* webpackChunkName: "DkIcon-vue" */ '@v4/common/components/atoms/DkIcon.vue'),
        }),
        DkButton: () => ({
            component: import(/* webpackChunkName: "DkButton-vue" */ '@v4/common/components/atoms/DkButton'),
        }),
    },
    mixins: [gaEventHandler],
    props: {
        /**
         * It is used in a card and does not have border on all sides
         */
        isInCard: {
            type: Boolean,
            default: false,
        },
        /**
         * Image URL for car image
         */
        imageUrl: {
            type: [String, null],
            default: null,
        },
        /**
         * Show header that contains box title and make + model of the car
         */
        showHeader: {
            type: Boolean,
            default: false,
        },
        /**
         * Make and model of the car
         */
        makeModel: {
            type: [String, null],
            default: null,
        },
        /**
         * Clickout box description/main text
         */
        description: {
            type: String,
            required: true,
        },
        /**
         * Button text; kept "ctaText" to be aligned with the backend property name
         */
        ctaText: {
            type: String,
            required: true,
        },
        /**
         * URL that the CTA button opens
         */
        url: {
            type: String,
            required: true,
        },
        /**
         * The page name on which the component
         * is used on; necessary for the GA event
         */
        originPageName: {
            type: String,
            required: true,
        },
        /**
         * Extra classes for the CTA button
         */
        ctaClasses: {
            type: String,
            default: null,
        },
        /**
         * Flag to show/hide icon in the button.
         */
        showIcon: {
            type: Boolean,
            default: true,
        },
        isCtaSmall: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {};
    },
    computed: {
        showSeparator() {
            return this.isInCard;
        },
        boxClasses() {
            const clickoutBoxClasses = [
                'lg:tw-flex lg:tw-items-center lg:tw-text-left tw-border-solid tw-border-border-color',
            ];

            const extraClasses = this.isInCard
                ? 'tw-mt-3 tw-justify-between tw-border-t'
                : 'tw-rounded tw-border tw-border-solid tw-border-border-color tw-bg-white hover:lg:tw-bg-secondary-light hover:lg:tw-border-placeholder tw-duration-300';

            const noImageClasses = this.imageUrl ? '' : 'lg:tw-justify-between';

            const padding = this.isInCard ? 'tw-pt-3 ' : 'tw-p-6';

            clickoutBoxClasses.push(extraClasses, noImageClasses, padding);

            return clickoutBoxClasses;
        },
        descriptionClasses() {
            const classes = 'tw-font-semibold tw-text-base tw-text-center body-sm';

            return this.isInCard
                ? 'tw-mb-2 lg:tw-mb-0 lg:tw-mr-4 micro tw-text-placeholder'
                : this.imageUrl
                    ? `${classes} tw-my-4 lg:tw-my-0 lg:tw-mx-4 lg:tw-text-left`
                    : `${classes} max-lg:tw-mb-4`;
        },
    },
    methods: {
        onClickRegisterGAEvent() {
            this.pushGaEvent(this.originPageName, 'click clickout', window.location.href);
        },
    },
};
</script>
